import { createContext, type RefObject, useContext, useEffect, useRef, useState } from 'react';
import { useOptionalDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';

/** Where keys belong to what has focus: a field, a terminal, an app's or a page's frame. */
function focusTakesKeys(element: Element | null): boolean {
  if (!(element instanceof HTMLElement)) return false;
  return (
    element.isContentEditable ||
    element.closest('[contenteditable="true"], [contenteditable=""]') !== null ||
    element.tagName === 'INPUT' ||
    element.tagName === 'TEXTAREA' ||
    element.tagName === 'SELECT' ||
    element.tagName === 'IFRAME' ||
    element.closest('.xterm, [data-terminal-surface]') !== null
  );
}

/** A menu, a dialog or a sheet is open over the page: Esc and ⌘↵ are theirs. */
function overlayOpen(): boolean {
  return (
    document.querySelector(
      '[role="dialog"][data-state="open"], [role="menu"], [data-slot="popover-content"], [data-composer-menu]',
    ) !== null
  );
}

/**
 * The conversation's composer has focus and nothing in it, in the dock's pane: right after a
 * message is sent. There ⌘↵ and Esc have nothing to do, so they are the dock's.
 */
function emptyComposerFocused(dock: HTMLElement): boolean {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement)) return false;
  const composer = active.closest('.desktop-composer');
  if (composer === null || !focusTakesKeys(active)) return false;
  const pane = dock.closest('[data-conversation-pane]');
  if (pane !== null && !pane.contains(composer)) return false;
  const text = active instanceof HTMLTextAreaElement ? active.value : (active.textContent ?? '');
  return text.trim() === '' && composer.querySelector('[data-reference-id]') === null;
}

/**
 * Which of a dock's keys reach it now: all of them (focus is in the dock, or nowhere and the dock
 * is the pane in use), only ⌘↵ and Esc (the pane's composer is focused and empty), or none.
 */
export type DockKeysReach = 'all' | 'composer' | 'none';

const DockKeysReachContext = createContext<DockKeysReach>('all');

export const DockKeysReachProvider = DockKeysReachContext.Provider;

/** Which keys of the dock around reach it now: its hints show only for those. */
function useDockKeysReach(): DockKeysReach {
  return useContext(DockKeysReachContext);
}

/** A key hint on a dock's button ("Esc", "⌘↵"), shown only while that key reaches the dock. */
export function Key({ children }: { children: string }) {
  const reach = useDockKeysReach();
  const works = reach === 'all' || (reach === 'composer' && children !== '↵');
  if (!works) return null;
  return (
    <kbd className="ml-1.5 rounded-md border border-hairline px-1 font-sans text-caption text-muted-foreground leading-4 max-md:hidden">
      {children}
    </kbd>
  );
}

export type DockKeys = {
  approve?: () => void;
  deny?: () => void;
  pick?: (index: number) => void;
  next?: () => void;
};

/**
 * Keyboard of a dock: ⌘↵ approves, Esc denies (or puts the question aside), digits pick a choice,
 * ↵ goes on. Keys reach it when focus is in the dock (it takes focus as it appears, unless the
 * user is typing somewhere), or when nothing has focus and the dock is its pane's, the pane in
 * use. From the pane's composer, focused and empty (right after sending), ⌘↵ and Esc reach it
 * too; digits and ↵ stay the composer's. Never from another field, a terminal or a frame, nor
 * over a menu or a dialog; in a split, only the active pane's dock.
 */
export function useDockKeys(handlers: DockKeys): {
  frameRef: RefObject<HTMLElement | null>;
  reach: DockKeysReach;
} {
  const frame = useRef<HTMLElement | null>(null);
  const latest = useRef(handlers);
  latest.current = handlers;
  const store = useOptionalDesktopShellStoreApi();
  const [reach, setReach] = useState<DockKeysReach>('all');
  useEffect(() => {
    const element = frame.current;
    if (element === null) return;
    // Its keys work at once, unless the user is in a field or a terminal.
    if (!focusTakesKeys(document.activeElement)) element.focus({ preventScroll: true });
    const activePane = () => {
      const pane = element
        .closest('[data-conversation-pane]')
        ?.getAttribute('data-conversation-pane');
      return pane == null || store === null || pane === store.getState().chatId;
    };
    const reachNow = (): DockKeysReach => {
      const active = document.activeElement;
      if (active !== null && element.contains(active)) return 'all';
      if ((active === null || active === document.body) && activePane()) return 'all';
      return emptyComposerFocused(element) ? 'composer' : 'none';
    };
    const handle = (event: KeyboardEvent, from: DockKeysReach) => {
      if (event.defaultPrevented || overlayOpen()) return;
      const { approve, deny, pick, next } = latest.current;
      const take = (run: () => void) => {
        event.preventDefault();
        // From the composer, it does not also act on the key.
        if (from === 'composer') event.stopPropagation();
        run();
      };
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey) && approve !== undefined)
        return take(approve);
      if (event.key === 'Escape' && deny !== undefined) return take(deny);
      if (from !== 'all' || event.metaKey || event.ctrlKey || event.altKey) return;
      if (pick !== undefined && /^[1-9]$/.test(event.key))
        return take(() => pick(Number(event.key) - 1));
      if (event.key === 'Enter' && !event.shiftKey && next !== undefined) take(next);
    };
    const inDock = (event: KeyboardEvent) => {
      // A field of the dock (its own answer) keeps its keys.
      if (event.target !== element && focusTakesKeys(event.target as Element)) return;
      handle(event, 'all');
    };
    // Nothing focused: after whatever else listens. The empty composer: before it, captured, so
    // its own ⌘↵ and Esc do not also run.
    const onWindow = (event: KeyboardEvent) => {
      const target = event.target;
      if (target instanceof Node && element.contains(target)) return;
      if (reachNow() === 'all') handle(event, 'all');
    };
    const onComposer = (event: KeyboardEvent) => {
      if (emptyComposerFocused(element)) handle(event, 'composer');
    };
    const update = () => setReach(reachNow());
    update();
    element.addEventListener('keydown', inDock);
    window.addEventListener('keydown', onWindow);
    window.addEventListener('keydown', onComposer, true);
    document.addEventListener('focusin', update);
    document.addEventListener('focusout', update);
    document.addEventListener('input', update);
    const unsubscribe = store?.subscribe(update);
    return () => {
      element.removeEventListener('keydown', inDock);
      window.removeEventListener('keydown', onWindow);
      window.removeEventListener('keydown', onComposer, true);
      document.removeEventListener('focusin', update);
      document.removeEventListener('focusout', update);
      document.removeEventListener('input', update);
      unsubscribe?.();
    };
  }, [store]);
  return { frameRef: frame, reach };
}
